import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import staffManagementService, { StaffMember, WorkShift, Holiday } from '../services/staffManagementService';
import TimeClock from '../components/staff/TimeClock';
import Documents from '../components/staff/Documents';
import PerformanceReviews from '../components/staff/PerformanceReviews';
import Analytics from '../components/staff/Analytics';
import OrgChart from '../components/staff/OrgChart';

// Import types from service - using API-compatible types

const StaffManagement: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'staff' | 'rota' | 'holidays' | 'coverage' | 'timeclock' | 'documents' | 'reviews' | 'analytics' | 'orgchart' | 'payroll'>('staff');
  const [loading, setLoading] = useState(false);
  const [organizationId] = useState<string | undefined>(undefined);
  
  // Real data from API
  const [staffMembers, setStaffMembers] = useState<StaffMember[]>([]);
  const [workShifts, setWorkShifts] = useState<WorkShift[]>([]);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [selectedStaff, setSelectedStaff] = useState<StaffMember | null>(null);
  const [showAddStaffModal, setShowAddStaffModal] = useState(false);
  const [showRequestLeaveModal, setShowRequestLeaveModal] = useState(false);

  // Load staff members from API
  useEffect(() => {
    const loadStaff = async () => {
      if (!user) return;
      try {
        setLoading(true);
        const { data } = await staffManagementService.listStaff(organizationId);
        setStaffMembers(data);
      } catch (err: any) {
        console.error('Failed to load staff:', err);
        toast.error('Failed to load staff members');
      } finally {
        setLoading(false);
      }
    };
    loadStaff();
  }, [user, organizationId]);

  // Load work shifts from API
  useEffect(() => {
    const loadShifts = async () => {
      if (!user || activeTab !== 'rota') return;
      try {
        const { data } = await staffManagementService.listShifts(organizationId);
        setWorkShifts(data);
      } catch (err: any) {
        console.error('Failed to load shifts:', err);
      }
    };
    loadShifts();
  }, [user, activeTab, organizationId]);

  // Load holidays from API
  useEffect(() => {
    const loadHolidays = async () => {
      if (!user || (activeTab !== 'holidays' && activeTab !== 'coverage')) return;
      try {
        const { data } = await staffManagementService.listHolidays(organizationId);
        setHolidays(data);
      } catch (err: any) {
        console.error('Failed to load holidays:', err);
      }
    };
    loadHolidays();
  }, [user, activeTab, organizationId]);

  React.useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  if (!user) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const getStaffName = (staffId: string) => {
    const staff = staffMembers.find(s => s.id === staffId);
    return staff ? `${staff.first_name} ${staff.last_name}` : 'Unknown';
  };

  const calculateCoverage = () => {
    const coverageByDay: Record<number, number> = {0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0};
    workShifts.forEach(shift => {
      const staff = staffMembers.find(s => s.id === shift.staff_id);
      if (staff && staff.status === 'active') {
        coverageByDay[shift.day_of_week] = (coverageByDay[shift.day_of_week] || 0) + 1;
      }
    });
    return coverageByDay;
  };

  const getHolidaysForStaff = (staffId: string) => {
    return holidays.filter(h => h.staff_id === staffId);
  };

  const handleAddStaff = async (newStaff: Partial<StaffMember>) => {
    try {
      setLoading(true);
      const staffData = {
        ...newStaff,
        organization_id: organizationId,
      };
      const { data } = await staffManagementService.createStaff(staffData);
      setStaffMembers([...staffMembers, data]);
      setShowAddStaffModal(false);
      toast.success('Staff member added successfully');
    } catch (err: any) {
      toast.error('Failed to add staff member');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestLeave = async (leaveData: Partial<Holiday>) => {
    try {
      setLoading(true);
      const holidayData = {
        ...leaveData,
        organization_id: organizationId,
        status: 'Pending' as const,
      };
      const { data } = await staffManagementService.createHoliday(holidayData);
      setHolidays([...holidays, data]);
      setShowRequestLeaveModal(false);
      toast.success('Leave request submitted successfully');
    } catch (err: any) {
      toast.error('Failed to submit leave request');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveHoliday = async (id: string) => {
    try {
      await staffManagementService.updateHoliday(id, { status: 'Approved' });
      setHolidays(holidays.map(h => h.id === id ? { ...h, status: 'Approved' as const } : h));
      toast.success('Holiday approved');
    } catch (err: any) {
      toast.error('Failed to approve holiday');
    }
  };

  const handleRejectHoliday = async (id: string) => {
    const reason = window.prompt('Reason for rejection:');
    if (!reason) return;
    try {
      await staffManagementService.updateHoliday(id, { 
        status: 'Rejected',
        rejection_reason: reason 
      });
      setHolidays(holidays.map(h => h.id === id ? { ...h, status: 'Rejected' as const } : h));
      toast.success('Holiday rejected');
    } catch (err: any) {
      toast.error('Failed to reject holiday');
    }
  };

  return (
    <section className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/legal-hr')}
            className="mb-4 flex items-center gap-2 text-blue-600 hover:text-blue-700"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Legal & HR Tools
          </button>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-600 to-rose-600">
              Staff Management
            </h1>
            <Link
              to="/help?article=staff-management-tabs"
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
            >
              ❓ Help
            </Link>
          </div>
          <p className="text-gray-600 mt-2">Manage your team, schedules, and leave requests</p>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-4 overflow-x-auto">
            {[
              { id: 'staff', label: 'Staff Directory', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' },
              { id: 'orgchart', label: 'Org Chart', icon: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z' },
              { id: 'timeclock', label: 'Time Clock', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
              { id: 'rota', label: 'Work Rota', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
              { id: 'holidays', label: 'Holidays', icon: 'M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9' },
              { id: 'payroll', label: 'Payroll Prep', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
              { id: 'documents', label: 'Documents', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
              { id: 'reviews', label: 'Reviews', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4' },
              { id: 'analytics', label: 'Analytics', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
              { id: 'coverage', label: 'Coverage', icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'border-pink-500 text-pink-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
                </svg>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          {!loading && activeTab === 'staff' && (
            <StaffDirectory 
              staffMembers={staffMembers} 
              setSelectedStaff={setSelectedStaff} 
              onAddStaff={() => setShowAddStaffModal(true)}
            />
          )}
          {activeTab === 'orgchart' && <OrgChart staffMembers={staffMembers} />}
          {activeTab === 'timeclock' && <TimeClock staffMembers={staffMembers} />}
          {activeTab === 'rota' && <WorkRota staffMembers={staffMembers} workShifts={workShifts} daysOfWeek={daysOfWeek} />}
          {activeTab === 'holidays' && (
            <HolidayRota 
              holidays={holidays} 
              getStaffName={getStaffName}
              onRequestLeave={() => setShowRequestLeaveModal(true)}
              onApprove={handleApproveHoliday}
              onReject={handleRejectHoliday}
            />
          )}
          {activeTab === 'payroll' && <PayrollPrep staffMembers={staffMembers} />}
          {activeTab === 'documents' && <Documents staffMembers={staffMembers} />}
          {activeTab === 'reviews' && <PerformanceReviews staffMembers={staffMembers} />}
          {activeTab === 'analytics' && <Analytics />}
          {activeTab === 'coverage' && <CoverageOverview calculateCoverage={calculateCoverage} daysOfWeek={daysOfWeek} />}
        </div>

        {/* Staff Detail Modal */}
        {selectedStaff && (
          <StaffDetailModal
            staff={selectedStaff}
            getHolidaysForStaff={getHolidaysForStaff}
            onClose={() => setSelectedStaff(null)}
          />
        )}

        {/* Add Staff Modal */}
        {showAddStaffModal && (
          <AddStaffModal
            onClose={() => setShowAddStaffModal(false)}
            onSubmit={handleAddStaff}
          />
        )}

        {/* Request Leave Modal */}
        {showRequestLeaveModal && (
          <RequestLeaveModal
            staffMembers={staffMembers}
            onClose={() => setShowRequestLeaveModal(false)}
            onSubmit={handleRequestLeave}
          />
        )}
      </div>
    </section>
  );
};

// Sub-components
const StaffDirectory: React.FC<{
  staffMembers: StaffMember[]; 
  setSelectedStaff: (staff: StaffMember) => void;
  onAddStaff: () => void;
}> = ({ staffMembers, setSelectedStaff, onAddStaff }) => (
  <div className="space-y-6">
    <div className="flex justify-between items-center">
      <h3 className="text-2xl font-bold text-gray-800">Staff Directory</h3>
      <button 
        onClick={onAddStaff}
        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        Add Staff Member
      </button>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {staffMembers.map(staff => (
        <div
          key={staff.id}
          className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition cursor-pointer border border-gray-100"
          onClick={() => setSelectedStaff(staff)}
        >
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-pink-400 to-rose-500 rounded-full flex items-center justify-center text-white text-xl font-bold">
              {staff.first_name[0]}{staff.last_name[0]}
            </div>
            <div className="flex-1">
              <h4 className="font-bold text-gray-800 text-lg">{staff.first_name} {staff.last_name}</h4>
              <p className="text-sm text-gray-600">{staff.position}</p>
              <p className="text-xs text-gray-500">{staff.department}</p>
            </div>
          </div>
          <div className="mt-4 space-y-2">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              {staff.email}
            </div>
            {staff.phone && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                {staff.phone}
              </div>
            )}
            <div className="pt-2 border-t">
              <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                staff.employment_type === 'Full-time' ? 'bg-green-100 text-green-800' :
                staff.employment_type === 'Part-time' ? 'bg-blue-100 text-blue-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {staff.employment_type} • {staff.weekly_hours || 0}h/week
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const WorkRota: React.FC<{
  staffMembers: StaffMember[]; 
  workShifts: WorkShift[]; 
  daysOfWeek: string[];
  onEditSchedule?: () => void;
}> = ({ staffMembers, workShifts, daysOfWeek, onEditSchedule }) => (
  <div className="space-y-6">
    <div className="flex justify-between items-center">
      <h3 className="text-2xl font-bold text-gray-800">Weekly Work Rota</h3>
      <div className="flex gap-2">
        <button 
          onClick={() => toast('PDF export coming soon!', { icon: '📄' })}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition text-sm"
        >
          Export PDF
        </button>
        <button 
          onClick={() => toast('Schedule editor coming soon!', { icon: '📅' })}
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition text-sm"
        >
          Edit Schedule
        </button>
      </div>
    </div>

    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gradient-to-r from-pink-500 to-rose-600 text-white">
          <tr>
            <th className="px-4 py-3 text-left font-semibold">Staff Member</th>
            {daysOfWeek.map(day => (
              <th key={day} className="px-4 py-3 text-center font-semibold">{day.slice(0, 3)}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {staffMembers.map(staff => (
            <tr key={staff.id} className="hover:bg-gray-50 transition">
              <td className="px-4 py-3 font-medium text-gray-800">
                {staff.first_name} {staff.last_name}
                <span className="block text-xs text-gray-500">{staff.position}</span>
              </td>
              {daysOfWeek.map((day, dayIndex) => {
                const shift = workShifts.find(s => s.staff_id === staff.id && s.day_of_week === dayIndex);
                return (
                  <td key={day} className="px-4 py-3 text-center">
                    {shift ? (
                      <div className="bg-pink-100 text-pink-800 rounded px-2 py-1 text-xs">
                        <div className="font-semibold">{shift.start_time.slice(0,5)}</div>
                        <div className="text-[10px]">{shift.end_time.slice(0,5)}</div>
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">Off</span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const HolidayRota: React.FC<{
  holidays: Holiday[]; 
  getStaffName: (id: string) => string;
  onRequestLeave: () => void;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
}> = ({ holidays, getStaffName, onRequestLeave, onApprove, onReject }) => (
  <div className="space-y-6">
    <div className="flex justify-between items-center">
      <h3 className="text-2xl font-bold text-gray-800">Holiday & Leave Management</h3>
      <button 
        onClick={onRequestLeave}
        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        Request Leave
      </button>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="bg-gradient-to-br from-green-400 to-green-500 rounded-xl p-6 text-white shadow-lg">
        <h4 className="text-sm font-semibold opacity-90 mb-2">Approved</h4>
        <p className="text-4xl font-bold">{holidays.filter(h => h.status === 'Approved').length}</p>
      </div>
      <div className="bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl p-6 text-white shadow-lg">
        <h4 className="text-sm font-semibold opacity-90 mb-2">Pending</h4>
        <p className="text-4xl font-bold">{holidays.filter(h => h.status === 'Pending').length}</p>
      </div>
      <div className="bg-gradient-to-br from-red-400 to-red-500 rounded-xl p-6 text-white shadow-lg">
        <h4 className="text-sm font-semibold opacity-90 mb-2">Rejected</h4>
        <p className="text-4xl font-bold">{holidays.filter(h => h.status === 'Rejected').length}</p>
      </div>
    </div>

    <table className="w-full">
      <thead className="bg-gradient-to-r from-green-500 to-teal-600 text-white">
        <tr>
          <th className="px-4 py-3 text-left">Staff</th>
          <th className="px-4 py-3 text-left">Type</th>
          <th className="px-4 py-3 text-left">Start</th>
          <th className="px-4 py-3 text-left">End</th>
          <th className="px-4 py-3 text-center">Days</th>
          <th className="px-4 py-3 text-center">Status</th>
          <th className="px-4 py-3 text-center">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {holidays.map(holiday => (
          <tr key={holiday.id} className="hover:bg-gray-50">
            <td className="px-4 py-3 font-medium">{getStaffName(holiday.staff_id)}</td>
            <td className="px-4 py-3">{holiday.type}</td>
            <td className="px-4 py-3">{new Date(holiday.start_date).toLocaleDateString()}</td>
            <td className="px-4 py-3">{new Date(holiday.end_date).toLocaleDateString()}</td>
            <td className="px-4 py-3 text-center">{holiday.days_count || 0}</td>
            <td className="px-4 py-3 text-center">
              <span className={`px-3 py-1 text-xs rounded-full ${
                holiday.status === 'Approved' ? 'bg-green-100 text-green-800' :
                holiday.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>
                {holiday.status}
              </span>
            </td>
            <td className="px-4 py-3 text-center">
              {holiday.status === 'Pending' && holiday.id && (
                <div className="flex gap-2 justify-center">
                  <button 
                    onClick={() => onApprove(holiday.id!)}
                    className="text-green-600 hover:text-green-700"
                    title="Approve"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </button>
                  <button 
                    onClick={() => onReject(holiday.id!)}
                    className="text-red-600 hover:text-red-700"
                    title="Reject"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const CoverageOverview: React.FC<{calculateCoverage: () => Record<number, number>; daysOfWeek: string[]}> = ({ calculateCoverage, daysOfWeek }) => {
  const coverage = calculateCoverage();
  const minCoverage = 2;

  return (
    <div className="space-y-6">
      <h3 className="text-2xl font-bold text-gray-800">Staff Coverage Overview</h3>

      <div className="grid grid-cols-2 md:grid-cols-7 gap-4">
        {daysOfWeek.map((day, idx) => {
          const count = coverage[idx] || 0;
          const isUnderstaffed = count < minCoverage;
          return (
            <div
              key={day}
              className={`rounded-lg p-4 text-center ${
                isUnderstaffed ? 'bg-red-100 border-2 border-red-300' : 'bg-green-100 border-2 border-green-300'
              }`}
            >
              <p className="font-semibold text-sm mb-2">{day}</p>
              <p className={`text-3xl font-bold ${isUnderstaffed ? 'text-red-600' : 'text-green-600'}`}>{count}</p>
              <p className="text-xs mt-1">{isUnderstaffed ? 'Low' : 'OK'}</p>
            </div>
          );
        })}
      </div>

      <div className="bg-white rounded-xl p-6 border">
        <h4 className="font-semibold mb-4">Coverage Alerts</h4>
        {Object.entries(coverage).filter(([_, count]) => count < minCoverage).length > 0 ? (
          Object.entries(coverage)
            .filter(([_, count]) => count < minCoverage)
            .map(([dayIdx, count]) => (
              <div key={dayIdx} className="bg-red-50 border border-red-200 rounded-lg p-3 mb-2">
                <p className="font-medium text-red-800">{daysOfWeek[parseInt(dayIdx)]} - Understaffed</p>
                <p className="text-sm text-red-600">Only {count} staff (need {minCoverage})</p>
              </div>
            ))
        ) : (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
            <p className="text-green-800">✓ All days have adequate coverage</p>
          </div>
        )}
      </div>
    </div>
  );
};

const RequestLeaveModal: React.FC<{
  staffMembers: StaffMember[];
  onClose: () => void; 
  onSubmit: (leave: Partial<Holiday>) => void
}> = ({ staffMembers, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    staff_id: '',
    type: 'Annual Leave' as const,
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    notes: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.staff_id || !formData.start_date || !formData.end_date) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (new Date(formData.end_date) < new Date(formData.start_date)) {
      toast.error('End date must be after start date');
      return;
    }
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-6">
          <h3 className="text-2xl font-bold">Request Leave</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Staff Member *</label>
            <select
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={formData.staff_id}
              onChange={e => setFormData({...formData, staff_id: e.target.value})}
            >
              <option value="">Select staff member</option>
              {staffMembers.map(staff => (
                <option key={staff.id} value={staff.id}>{staff.first_name} {staff.last_name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Leave Type *</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={formData.type}
              onChange={e => setFormData({...formData, type: e.target.value as any})}
            >
              <option value="Annual Leave">Annual Leave</option>
              <option value="Sick Leave">Sick Leave</option>
              <option value="Unpaid Leave">Unpaid Leave</option>
              <option value="Public Holiday">Public Holiday</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
              <input
                type="date"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.start_date}
                onChange={e => setFormData({...formData, start_date: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
              <input
                type="date"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.end_date}
                onChange={e => setFormData({...formData, end_date: e.target.value})}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={formData.notes}
              onChange={e => setFormData({...formData, notes: e.target.value})}
              placeholder="Add any additional information..."
            />
          </div>

          <div className="flex gap-3 justify-end pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              Submit Request
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AddStaffModal: React.FC<{onClose: () => void; onSubmit: (staff: Partial<StaffMember>) => void}> = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    position: '',
    department: '',
    start_date: new Date().toISOString().split('T')[0],
    employment_type: 'Full-time' as const,
    weekly_hours: 40,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.position) {
      toast.error('Please fill in all required fields');
      return;
    }
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-6">
          <h3 className="text-2xl font-bold">Add New Staff Member</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
              <input
                type="text"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.first_name}
                onChange={e => setFormData({...formData, first_name: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
              <input
                type="text"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.last_name}
                onChange={e => setFormData({...formData, last_name: e.target.value})}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <input
              type="email"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={formData.email}
              onChange={e => setFormData({...formData, email: e.target.value})}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input
              type="tel"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={formData.phone}
              onChange={e => setFormData({...formData, phone: e.target.value})}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Position *</label>
              <input
                type="text"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.position}
                onChange={e => setFormData({...formData, position: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.department}
                onChange={e => setFormData({...formData, department: e.target.value})}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employment Type</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.employment_type}
                onChange={e => setFormData({...formData, employment_type: e.target.value as any})}
              >
                <option value="Full-time">Full-time</option>
                <option value="Part-time">Part-time</option>
                <option value="Contract">Contract</option>
                <option value="Intern">Intern</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Weekly Hours</label>
              <input
                type="number"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.weekly_hours}
                onChange={e => setFormData({...formData, weekly_hours: parseFloat(e.target.value)})}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={formData.start_date}
              onChange={e => setFormData({...formData, start_date: e.target.value})}
            />
          </div>

          <div className="flex gap-3 justify-end pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              Add Staff Member
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const StaffDetailModal: React.FC<{staff: StaffMember; getHolidaysForStaff: (id: string) => Holiday[]; onClose: () => void}> = ({ staff, getHolidaysForStaff, onClose }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
    <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
      <div className="flex justify-between items-start mb-6">
        <div>
          <h3 className="text-2xl font-bold">{staff.first_name} {staff.last_name}</h3>
          <p className="text-gray-600">{staff.position} • {staff.department}</p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="space-y-6">
        <div>
          <h4 className="font-semibold mb-3">Contact Information</h4>
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <p className="text-sm"><span className="font-medium">Email:</span> {staff.email}</p>
            {staff.phone && <p className="text-sm"><span className="font-medium">Phone:</span> {staff.phone}</p>}
            <p className="text-sm"><span className="font-medium">Start Date:</span> {new Date(staff.start_date).toLocaleDateString()}</p>
          </div>
        </div>

        <div>
          <h4 className="font-semibold mb-3">Emergency Contacts</h4>
          {staff.emergency_contacts && staff.emergency_contacts.length > 0 ? (
            staff.emergency_contacts.map((contact: any, idx: number) => (
              <div key={idx} className="bg-red-50 border border-red-100 rounded-lg p-4 space-y-2 mb-2">
                <p className="font-medium text-red-900">{contact.name} ({contact.relationship})</p>
                <p className="text-sm text-red-800">Phone: {contact.phone}</p>
                {contact.email && <p className="text-sm text-red-800">Email: {contact.email}</p>}
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-600">No emergency contacts</p>
          )}
        </div>

        <div>
          <h4 className="font-semibold mb-3">Holiday Summary</h4>
          <div className="bg-blue-50 rounded-lg p-4">
            {staff.id && getHolidaysForStaff(staff.id).length > 0 ? (
              <div className="space-y-2">
                {staff.id && getHolidaysForStaff(staff.id).map(holiday => (
                  <div key={holiday.id} className="flex justify-between items-center">
                    <div>
                      <p className="text-sm font-medium">{holiday.type}</p>
                      <p className="text-xs text-gray-600">
                        {new Date(holiday.start_date).toLocaleDateString()} - {new Date(holiday.end_date).toLocaleDateString()}
                      </p>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      holiday.status === 'Approved' ? 'bg-green-100 text-green-800' :
                      holiday.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {holiday.status}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-600">No holidays scheduled</p>
            )}
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Payroll Prep Component - Phase 2 & 3 Complete
const PayrollPrep: React.FC<{ staffMembers: StaffMember[] }> = ({ staffMembers }) => {
  const { user } = useAuth();
  const [selectedPeriodId, setSelectedPeriodId] = useState<string | null>(null);
  const [periods, setPeriods] = useState<any[]>([]);
  const [periodEntries, setPeriodEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [newPeriod, setNewPeriod] = useState({ period_start: '', period_end: '', payment_date: '' });
  
  // Load payroll periods
  React.useEffect(() => {
    const loadPeriods = async () => {
      if (!user) return;
      const { data } = await staffManagementService.getPayrollPeriods();
      setPeriods(data || []);
      if (data && data.length > 0 && !selectedPeriodId) {
        setSelectedPeriodId(data[0].id);
      }
    };
    loadPeriods();
  }, [user]);
  
  // Load period entries when period selected
  React.useEffect(() => {
    const loadEntries = async () => {
      if (!selectedPeriodId) return;
      setLoading(true);
      try {
        const { data } = await staffManagementService.getPayrollEntries(selectedPeriodId);
        setPeriodEntries(data || []);
      } catch (err) {
        console.error('Error loading payroll entries:', err);
      } finally {
        setLoading(false);
      }
    };
    loadEntries();
  }, [selectedPeriodId]);
  
  const currentPeriod = periods.find(p => p.id === selectedPeriodId);
  
  const handleCreatePeriod = async () => {
    try {
      const { data, error } = await staffManagementService.createPayrollPeriod(newPeriod);
      if (error) throw error;
      
      toast.success('Payroll period created');
      setPeriods([data, ...periods]);
      setSelectedPeriodId(data.id);
      setShowCreateModal(false);
      setNewPeriod({ period_start: '', period_end: '', payment_date: '' });
    } catch (err: any) {
      toast.error(`Failed to create period: ${err.message}`);
    }
  };
  
  const handleGeneratePayroll = async () => {
    if (!selectedPeriodId) return;
    setGenerating(true);
    try {
      const { data, error } = await staffManagementService.generatePayrollEntries(selectedPeriodId);
      if (error) throw error;
      
      toast.success(`Generated payroll for ${data.length} staff members`);
      // Reload entries
      const { data: entries } = await staffManagementService.getPayrollEntries(selectedPeriodId);
      setPeriodEntries(entries || []);
    } catch (err: any) {
      toast.error(`Failed to generate payroll: ${err.message}`);
    } finally {
      setGenerating(false);
    }
  };
  
  const handleApprovePeriod = async () => {
    if (!selectedPeriodId) return;
    try {
      await staffManagementService.approvePayrollPeriod(selectedPeriodId);
      toast.success('Payroll period approved');
      // Reload periods
      const { data } = await staffManagementService.getPayrollPeriods();
      setPeriods(data || []);
    } catch (err: any) {
      toast.error(`Failed to approve: ${err.message}`);
    }
  };
  
  const handleReopenPeriod = async () => {
    if (!selectedPeriodId) return;
    try {
      await staffManagementService.reopenPayrollPeriod(selectedPeriodId);
      toast.success('Payroll period reopened');
      const { data } = await staffManagementService.getPayrollPeriods();
      setPeriods(data || []);
    } catch (err: any) {
      toast.error(`Failed to reopen: ${err.message}`);
    }
  };
  
  const handleExportToCSV = () => {
    if (periodEntries.length === 0) {
      toast.error('No payroll entries to export');
      return;
    }
    
    const csvData = periodEntries.map(entry => ({
      'Employee Name': `${entry.staff.first_name} ${entry.staff.last_name}`,
      'Position': entry.staff.position,
      'Department': entry.staff.department || '',
      'Regular Hours': entry.regular_hours?.toFixed(2) || '0.00',
      'Overtime Hours': entry.overtime_hours?.toFixed(2) || '0.00',
      'Holiday Hours': entry.holiday_hours?.toFixed(2) || '0.00',
      'Sick Hours': entry.sick_hours?.toFixed(2) || '0.00',
      'Total Hours': ((entry.regular_hours || 0) + (entry.overtime_hours || 0) + (entry.holiday_hours || 0) + (entry.sick_hours || 0)).toFixed(2),
      'Hourly Rate': entry.hourly_rate?.toFixed(2) || '0.00',
      'Gross Pay': entry.gross_pay?.toFixed(2) || '0.00',
      'Period Start': currentPeriod?.period_start || '',
      'Period End': currentPeriod?.period_end || '',
      'Payment Date': currentPeriod?.payment_date || ''
    }));
    
    const csv = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payroll-${currentPeriod?.period_start}-${currentPeriod?.period_end}.csv`;
    a.click();
    
    toast.success('Payroll data exported to CSV');
  };
  
  const totalHours = periodEntries.reduce((sum, e) => sum + (e.regular_hours || 0) + (e.overtime_hours || 0) + (e.holiday_hours || 0) + (e.sick_hours || 0), 0);
  const totalCost = periodEntries.reduce((sum, e) => sum + (e.gross_pay || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header with Action Buttons */}
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-2xl font-bold text-gray-800">Payroll Management</h3>
          <p className="text-sm text-gray-600 mt-1">Period-based payroll with overtime, holiday & sick pay calculation</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-pink-600 text-white px-4 py-2 rounded-lg hover:bg-pink-700 transition flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            New Period
          </button>
        </div>
      </div>

      {/* Period Selector & Status */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Pay Period</label>
            <select
              value={selectedPeriodId || ''}
              onChange={(e) => setSelectedPeriodId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500"
            >
              <option value="">-- Select a period --</option>
              {periods.map(period => (
                <option key={period.id} value={period.id}>
                  {new Date(period.period_start).toLocaleDateString()} - {new Date(period.period_end).toLocaleDateString()} 
                  ({period.status})
                </option>
              ))}
            </select>
          </div>
          {currentPeriod && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Period Status</label>
              <div className="flex items-center gap-3">
                {currentPeriod.status === 'open' && (
                  <span className="px-3 py-2 bg-blue-100 text-blue-800 rounded-lg text-sm font-medium">
                    📝 Open - Editable
                  </span>
                )}
                {currentPeriod.status === 'approved' && (
                  <span className="px-3 py-2 bg-green-100 text-green-800 rounded-lg text-sm font-medium">
                    ✅ Approved
                  </span>
                )}
                {currentPeriod.status === 'processing' && (
                  <span className="px-3 py-2 bg-yellow-100 text-yellow-800 rounded-lg text-sm font-medium">
                    ⏳ Processing
                  </span>
                )}
                <span className="text-sm text-gray-600">Payment: {new Date(currentPeriod.payment_date).toLocaleDateString()}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Period Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4">Create Payroll Period</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Period Start</label>
                <input type="date" required
                  value={newPeriod.period_start}
                  onChange={e => setNewPeriod({...newPeriod, period_start: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Period End</label>
                <input type="date" required
                  value={newPeriod.period_end}
                  onChange={e => setNewPeriod({...newPeriod, period_end: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Payment Date</label>
                <input type="date" required
                  value={newPeriod.payment_date}
                  onChange={e => setNewPeriod({...newPeriod, payment_date: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowCreateModal(false)} className="flex-1 border rounded-lg py-2">
                Cancel
              </button>
              <button onClick={handleCreatePeriod} className="flex-1 bg-pink-600 text-white rounded-lg py-2">
                Create Period
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons - only show if period selected */}
      {currentPeriod && (
        <div className="flex gap-3">
          <button
            onClick={handleGeneratePayroll}
            disabled={generating || currentPeriod.status === 'approved'}
            className="bg-pink-600 text-white px-4 py-2 rounded-lg hover:bg-pink-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {generating ? 'Generating...' : 'Generate Payroll'}
          </button>
          <button
            onClick={handleExportToCSV}
            disabled={periodEntries.length === 0}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition disabled:opacity-50 flex items-center gap-2"
          >
            Export CSV
          </button>
          {currentPeriod.status === 'open' && periodEntries.length > 0 && (
            <button
              onClick={handleApprovePeriod}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
            >
              Approve Period
            </button>
          )}
          {currentPeriod.status === 'approved' && (
            <button
              onClick={handleReopenPeriod}
              className="bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 transition"
            >
              Reopen Period
            </button>
          )}
        </div>
      )}

      {/* Summary Cards */}
      {currentPeriod && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-pink-400 to-rose-500 rounded-xl p-6 text-white shadow-lg">
            <h4 className="text-sm font-semibold opacity-90 mb-2">Staff Members</h4>
            <p className="text-4xl font-bold">{periodEntries.length}</p>
          </div>
          <div className="bg-gradient-to-br from-blue-400 to-blue-500 rounded-xl p-6 text-white shadow-lg">
            <h4 className="text-sm font-semibold opacity-90 mb-2">Total Hours</h4>
            <p className="text-4xl font-bold">{totalHours.toFixed(0)}</p>
            <p className="text-xs opacity-75 mt-1">Regular + OT + Holiday + Sick</p>
          </div>
          <div className="bg-gradient-to-br from-green-400 to-green-500 rounded-xl p-6 text-white shadow-lg">
            <h4 className="text-sm font-semibold opacity-90 mb-2">Total Cost</h4>
            <p className="text-4xl font-bold">£{totalCost.toFixed(0)}</p>
            <p className="text-xs opacity-75 mt-1">Before tax & NI</p>
          </div>
          <div className="bg-gradient-to-br from-purple-400 to-purple-500 rounded-xl p-6 text-white shadow-lg">
            <h4 className="text-sm font-semibold opacity-90 mb-2">Status</h4>
            <p className="text-2xl font-bold">{currentPeriod.status.toUpperCase()}</p>
            <p className="text-xs opacity-75 mt-1">{periodEntries.length} entries</p>
          </div>
        </div>
      )}

      {/* Payroll Entries Table - Phase 3 with Overtime/Holiday/Sick */}
      {currentPeriod && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {periodEntries.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-600 mb-4">No payroll entries generated for this period yet.</p>
              <button
                onClick={handleGeneratePayroll}
                className="bg-pink-600 text-white px-6 py-2 rounded-lg hover:bg-pink-700 transition"
              >
                Generate Payroll Now
              </button>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gradient-to-r from-pink-500 to-rose-600 text-white">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Employee</th>
                  <th className="px-4 py-3 text-center font-semibold">Regular</th>
                  <th className="px-4 py-3 text-center font-semibold">Overtime (1.5x)</th>
                  <th className="px-4 py-3 text-center font-semibold">Holiday</th>
                  <th className="px-4 py-3 text-center font-semibold">Sick</th>
                  <th className="px-4 py-3 text-center font-semibold">Total Hours</th>
                  <th className="px-4 py-3 text-right font-semibold">Gross Pay</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {periodEntries.map((entry, idx) => {
                  const totalHrs = (entry.regular_hours || 0) + (entry.overtime_hours || 0) + (entry.holiday_hours || 0) + (entry.sick_hours || 0);
                  return (
                    <tr key={idx} className="hover:bg-gray-50 transition">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-800">
                          {entry.staff?.first_name} {entry.staff?.last_name}
                        </div>
                        <div className="text-xs text-gray-500">{entry.staff?.position}</div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-sm">{(entry.regular_hours || 0).toFixed(1)}h</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {entry.overtime_hours > 0 ? (
                          <span className="inline-block px-2 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-medium">
                            {entry.overtime_hours.toFixed(1)}h
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {entry.holiday_hours > 0 ? (
                          <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                            {entry.holiday_hours.toFixed(1)}h
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {entry.sick_hours > 0 ? (
                          <span className="inline-block px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs">
                            {entry.sick_hours.toFixed(1)}h
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="font-semibold text-gray-800">{totalHrs.toFixed(1)}h</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-lg font-bold text-green-600">
                          £{(entry.gross_pay || 0).toFixed(2)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr className="font-bold">
                  <td className="px-4 py-3 text-right">TOTALS:</td>
                  <td className="px-4 py-3 text-center text-gray-700">
                    {periodEntries.reduce((sum, e) => sum + (e.regular_hours || 0), 0).toFixed(1)}h
                  </td>
                  <td className="px-4 py-3 text-center text-amber-700">
                    {periodEntries.reduce((sum, e) => sum + (e.overtime_hours || 0), 0).toFixed(1)}h
                  </td>
                  <td className="px-4 py-3 text-center text-blue-700">
                    {periodEntries.reduce((sum, e) => sum + (e.holiday_hours || 0), 0).toFixed(1)}h
                  </td>
                  <td className="px-4 py-3 text-center text-red-700">
                    {periodEntries.reduce((sum, e) => sum + (e.sick_hours || 0), 0).toFixed(1)}h
                  </td>
                  <td className="px-4 py-3 text-center text-gray-800">
                    {totalHours.toFixed(1)}h
                  </td>
                  <td className="px-4 py-3 text-right text-green-600 text-xl">
                    £{totalCost.toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      )}

      {/* Feature Summary */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200 p-6">
        <h4 className="font-bold text-blue-900 mb-4 flex items-center gap-2">
          <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Payroll Features Included
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="font-semibold text-gray-800">Period Management</p>
              <p className="text-sm text-gray-600">Create, track, and approve payroll periods</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="font-semibold text-gray-800">Overtime Calculation</p>
              <p className="text-sm text-gray-600">Automatic 1.5x rate for hours over 40/week</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="font-semibold text-gray-800">Holiday Pay Integration</p>
              <p className="text-sm text-gray-600">Pulls approved annual leave from holidays module</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="font-semibold text-gray-800">Sick Pay Tracking</p>
              <p className="text-sm text-gray-600">Tracks sick leave hours from holidays module</p>
            </div>
          </div>
        </div>
      </div>

      {/* Important Notice */}
      <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-amber-500 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div>
            <h4 className="text-sm font-semibold text-amber-800 mb-1">⚠️ Gross Pay Calculation Only</h4>
            <p className="text-sm text-amber-700">
              This shows gross pay with overtime, holiday, and sick pay included. <strong>Tax, National Insurance, and pension deductions 
              are NOT calculated.</strong> Export to CSV and process through your payroll provider (Xero, BrightPay, Sage) for full PAYE compliance.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffManagement;
